import Grid from '@amzn/awsui-components-react/polaris/grid';
import Container from '@amzn/awsui-components-react/polaris/container';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import Button from '@amzn/awsui-components-react/polaris/button';
import Header from '@amzn/awsui-components-react/polaris/header';
import Box from '@amzn/awsui-components-react/polaris/box';
import Select from '@amzn/awsui-components-react/polaris/select';
import React, {useState, useEffect} from 'react';
import { textAlign } from 'html2canvas/dist/types/css/property-descriptors/text-align';
import Alert from '@amzn/awsui-components-react/polaris/alert';
import { usePlayground } from '../../hooks/usePlayground';
import { getUserAlias } from '../../auth/MidwayJwtToken';
import notebook_list from '../../../notebooksConfig.json'
import { InternalTokenGroup } from '@amzn/awsui-components-react/polaris/property-filter/interfaces';

// Notebook info that in the config file
interface NotebookConfig{
  id: number,
  name: string,
  description: string,
  portal_name: string
}

// The content in the main content area of the App layout
export default function Playground() {
  
  const [username, setUsername] = useState<string>('default username');

  const [lastAction, setLastAction] = useState<string>('');

  const [playgroundParams, setPlaygroundParams] = useState({
    notebook_name: '',
    username: ''
  });

  // Tracking fetch state
  const [shouldFetch, setShouldFetch] = useState(false);

  const [consoleOpened, setConsoleOpened] = useState(false);

  const [showStatusAlert, setShowStatusAlert] = useState(false);

  const { data, isLoading, error } = usePlayground(playgroundParams, shouldFetch);

  const getStatusAlert = (status: string) => {
    switch (status) {
      case 'Stopped':
        return { type: 'warning' as const, message: 'Service was stopped. Starting it up for you.' };
      case 'Pending':
        return { type: 'warning' as const, message: 'Service is starting up. Please wait...' };
      case 'Stopping':
        return { type: 'warning' as const, message: 'Service is being stopped. Please wait before trying to start it again.' };
      case 'Failed':
        return { type: 'error' as const, message: 'Service status is failed. ¯\_(ツ)_/¯' };
      case 'Deleting':
        return { type: 'error' as const, message: 'Service is currently unavailable.' };
      default:
        return { type: 'error' as const, message: 'Service status unknown.' };
    }
  };

  // Button Click Handlers
  const handleButtonClick = (actionName: string) => {
    console.log(`Button clicked: ${actionName}`);
    console.log(`Username associated with request is: ${username}`);
    setLastAction(actionName);
  
    setPlaygroundParams(prev => ({
        ...prev,
        notebook_name: actionName,
      }));
  
    setShouldFetch(true);
  };

  // Effect to open URL when data is available
  useEffect(() => {
    if (data && data.console_url && data.notebook_url && data.status) {
      // Only open console_url which logs in the user if it hasn't been opened before
      if (data.status !== 'InService'){
        setShowStatusAlert(true);
      }
      else{
        if (!consoleOpened) {
          const tab = window.open(data.console_url, '_blank');
          setConsoleOpened(true);
          tab?.close();
          }
        window.open(data.notebook_url, '_blank');
      }
      setShouldFetch(false);
    }
  }, [data]);

  // Effect to save username/alias into playground params
  useEffect(() => {
    const fetchUsername = async () => {
      try{
        const alias = await getUserAlias();
        setUsername(alias);

        setPlaygroundParams(prev => ({
          ...prev,
          username: alias
        }));
      } catch (e){
        console.error("Error fetching username:", e);
      }
    };
    fetchUsername();
  }, []);

  const responsiveMargin = {
    marginTop: 'clamp(15px, 3vh, 40px)'  // Min 15px, max 40px, scales with viewport height
  };

  return (
    <>
    <div style={responsiveMargin}>
      <Grid
        gridDefinition={[
          {colspan: { xl: 8, l: 8, s: 8, xxs: 10 }, offset: { xl: 2, l: 2, s: 2,xxs: 1 }}

        ]}
      >
        <SpaceBetween size="l">
            <SpaceBetween size="m">
              
              {/* Info Box */}
              <Alert
                type="info"
                header="Note"
                dismissible={false}
              >
                <p>
                  * This notebook playground streamlines access to the Jupyter notebooks that give valuable insights into the performance of our models. <br /><br />

                  * Selecting the notebook playground button will open up a tab to the AWS account that hosts the playground instance. <b>Turn on multi-session support in your AWS account to avoid getting logged out of other sessions.</b> <br /><br />

                  * For steps to get started and additional information, check out the <a href="https://quip-amazon.com/KasbA1udlySg/Notebook-Playground-User-Guide" target="_blank" rel="noopener noreferrer"> Notebook Playground - User Guide </a>
                </p>
                
              </Alert>

              {/* Status Alert */}
              {(showStatusAlert) && data?.status && (
                <Alert
                  type={getStatusAlert(data.status).type}
                  header="Service Status"
                  dismissible={true}
                  onDismiss={() => { setShowStatusAlert(false);}}
                >
                  {getStatusAlert(data.status).message} Status: {data.status}
                </Alert>
              )}
              
              <Box textAlign = 'center'> 
                <SpaceBetween size="m" direction="vertical">
                  
                  <SpaceBetween size="m" direction="vertical">
                    {/* Dynamic generation of notebook buttons based on notebooksConfig.json file */}
                    {notebook_list.notebooks.map((notebook: NotebookConfig) => (
                      <Button 
                        key = {notebook.id}
                        onClick={() => handleButtonClick(notebook.name)}
                        disabled={isLoading}
                        variant = "primary"
                        >
                        {notebook.portal_name}
                      </Button>
                    ))}
                  </SpaceBetween>
                </SpaceBetween>
              </Box>
            </SpaceBetween>
        </SpaceBetween>
      </Grid>
    </div>
    </>
  );
}